/**
 * Pure helpers for the home "Free right now" strip: which rooms to offer and
 * the one-tap booking each card makes.
 */
import type { FreeRoomView } from '@hub-wifi/shared'
import { DEFAULT_DURATION, SLOT_MINUTES } from './book-sheet'

/** Shortest booking the service accepts; shorter free runs aren't offered. */
const MIN_MINUTES = 30

export interface FreeNowOffer {
  room: FreeRoomView
  /** Booking length the card's button makes: the default hour, or less when
   *  the room is only free for part of it (on the 15-min grid, ≥ 30 min). */
  minutes: number
  /** Whole free run from the start, in minutes. */
  freeMinutes: number
}

/**
 * Rooms free from `startMs` for at least 30 min, longest free runs first
 * (ties keep server order), capped at `limit`.
 */
export function freeNowOffers(rooms: FreeRoomView[], startMs: number, limit = 6): FreeNowOffer[] {
  return rooms
    .map((room, i) => {
      if (!room.free || !room.freeUntil) return null
      const freeMinutes = Math.floor((new Date(room.freeUntil).getTime() - startMs) / 60_000)
      if (freeMinutes < MIN_MINUTES) return null
      const minutes = Math.min(
        DEFAULT_DURATION,
        Math.floor(freeMinutes / SLOT_MINUTES) * SLOT_MINUTES,
      )
      return { offer: { room, minutes, freeMinutes }, i }
    })
    .filter((x): x is { offer: FreeNowOffer; i: number } => x !== null)
    .sort((a, b) => b.offer.freeMinutes - a.offer.freeMinutes || a.i - b.i)
    .slice(0, limit)
    .map(({ offer }) => offer)
}
